import React from "react";
import styled from "@emotion/styled";
import { Typography } from "../Typography";
import {
  CarbonAvoidanceProjectType,
  ProjectType,
  projectTypeMapper,
} from "@ecologi/shared";
import Forestry from "@ecologi/assets/icons/ProjectInsights/forestry.svg";
import Capture from "@ecologi/assets/icons/ProjectInsights/capture.svg";
import Wetlands from "@ecologi/assets/icons/ProjectInsights/wetlands.svg";
import Renewables from "@ecologi/assets/icons/ProjectInsights/renewables.svg";
import Community from "@ecologi/assets/icons/ProjectInsights/community.svg";
import Transport from "@ecologi/assets/icons/ProjectInsights/transport.svg";
import numeral from "numeral";

const ProjectTypeCardMapper: Record<
  Exclude<CarbonAvoidanceProjectType, "all_avoidance" | "no_avoidance">,
  { color: string; icon: JSX.Element }
> = {
  [CarbonAvoidanceProjectType.Capture]: {
    color: "#FADC86",
    icon: <Capture />,
  },
  [CarbonAvoidanceProjectType.Community]: {
    color: "#FEC6CE",
    icon: <Community />,
  },
  [CarbonAvoidanceProjectType.Forestry]: {
    color: "#94E1C1",
    icon: <Forestry />,
  },
  [CarbonAvoidanceProjectType.Geothermal]: {
    // TODO: [Impact-Types] Geothermal needs icon and likely different colour
    color: "#DCD3EF",
    icon: <Renewables />,
  },
  [CarbonAvoidanceProjectType.Renewables]: {
    color: "#DCD3EF",
    icon: <Renewables />,
  },
  [CarbonAvoidanceProjectType.Transport]: {
    color: "#FFCAA7",
    icon: <Transport />,
  },
  [CarbonAvoidanceProjectType.Wetlands]: {
    color: "#A5E7F5",
    icon: <Wetlands />,
  },
};

const ProjectTypeCardWrapper = styled.div<{
  variant: ProjectType;
}>`
  background-color: ${(props) =>
    (props.variant && ProjectTypeCardMapper[props.variant]?.color) ||
    "#D3D3D3"};
  width: 100%;
  padding: 8px 14px 8px 24px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-height: 80px;
`;

const PrimaryTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const IconWrapper = styled.div`
  width: 64px;
`;

const SecondaryTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  padding: 10px 14px;
  background-color: ${(props) => props.theme.colors.snowdrop};
`;

const CarbonTotalText = styled(Typography)`
  width: 64px;
  text-align: center;
`;

export type ProjectTypeCardProps = {
  variant: ProjectType;
  carbonTotal: number;
};

export const ProjectTypeCard = ({
  variant,
  carbonTotal,
}: ProjectTypeCardProps) => {
  return (
    <div>
      <ProjectTypeCardWrapper variant={variant}>
        <PrimaryTextWrapper>
          <Typography use="label" as="p">
            Solution type:
          </Typography>
          <Typography use="textInputLabel" as="p">
            {projectTypeMapper[variant].title}
          </Typography>
        </PrimaryTextWrapper>
        <IconWrapper>
          {ProjectTypeCardMapper[variant]?.icon || null}
        </IconWrapper>
      </ProjectTypeCardWrapper>
      <SecondaryTextWrapper>
        <Typography use="dashboardBody" as="p">
          Your impact (tCO2e avoided):
        </Typography>
        <CarbonTotalText use="dashboardMicroTitle" as="p">
          {numeral(carbonTotal).format("0.0a")}
        </CarbonTotalText>
      </SecondaryTextWrapper>
    </div>
  );
};
